import { useEffect, useMemo, useState } from "react";
import { SmartTable } from "@/components/SmartTable";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowDownRight, ArrowUpRight, Loader2, Minus } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilterBar, PageHeader } from "@/components/FilterBar";
import { Panel } from "@/components/KpiCard";
import { useFilters } from "@/context/FilterContext";
import { api, cleanParams, errMsg, money, moisLabel } from "@/lib/api";

// good: "up" = hausse favorable, "down" = baisse favorable
const METRICS = [
  ["revenus_bruts", "Revenus bruts", "money", "up"],
  ["revenus_airbnb", "dont Airbnb", "money", "up"],
  ["revenus_booking", "dont Booking", "money", "up"],
  ["nb_reservations", "Réservations", "int", "up"],
  ["nuits", "Nuits louées", "int", "up"],
  ["commissions", "Commissions", "money", "down"],
  ["taux_commission", "Taux de commission", "pct", "down"],
  ["total_verse", "Total versé net", "money", "up"],
  ["taxes_versees", "Taxes remises", "money", "up"],
  ["taxes_attendues", "Taxes attendues", "money", "up"],
  ["ecart_fiscal", "Écart fiscal à régulariser", "money", "down"],
  ["nb_erreurs", "Réservations en erreur", "int", "down"],
  ["depots", "Dépôts bancaires", "money", "up"],
  ["depenses", "Dépenses bancaires", "money", "down"],
];

const fmtVal = (v, kind) => (kind === "money" ? money(v) : kind === "pct" ? `${String(v).replace(".", ",")} %` : v);

const Variation = ({ v, good, testId }) => {
  if (v === null || v === undefined) return <span className="text-xs text-slate-400" data-testid={testId}>—</span>;
  if (v === 0) return <span className="inline-flex items-center text-xs text-slate-500" data-testid={testId}><Minus className="h-3 w-3 mr-1" />0 %</span>;
  const favorable = (v > 0) === (good === "up");
  const Icon = v > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center text-xs font-semibold tabular ${favorable ? "text-emerald-700" : "text-red-600"}`} data-testid={testId}>
      <Icon className="h-3.5 w-3.5 mr-0.5" />{v > 0 ? "+" : ""}{String(v).replace(".", ",")} %
    </span>
  );
};

const MonthSelect = ({ id, value, onChange, months }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9 w-40 bg-white" data-testid={`compare-${id}-select`}><SelectValue placeholder="Choisir un mois" /></SelectTrigger>
    <SelectContent>{months.map((m) => <SelectItem key={m} value={m} data-testid={`compare-${id}-${m}`}>{moisLabel(m)}</SelectItem>)}</SelectContent>
  </Select>
);

export default function Comparaison() {
  const { filters, options } = useFilters();
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!options.mois.length) return;
    setB((x) => x || options.mois[0]);
    setA((x) => x || options.mois[1] || options.mois[0]);
  }, [options.mois]);

  useEffect(() => {
    if (!a || !b) return;
    api.get("/comparaison", { params: cleanParams({ mois_a: a, mois_b: b, plateforme: filters.plateforme, logement: filters.logement }) })
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [a, b, filters.plateforme, filters.logement]);

  const chart = useMemo(() => data && [
    ["Revenus", "revenus_bruts"], ["Commissions", "commissions"], ["Taxes remises", "taxes_versees"], ["Écart fiscal", "ecart_fiscal"],
  ].map(([label, k]) => ({ label, A: data.a[k], B: data.b[k] })), [data]);

  return (
    <div data-testid="comparaison-page">
      <PageHeader eyebrow="Analyse" title="Comparaison de mois">
        <div className="flex flex-col gap-2 items-start xl:items-end">
          <div className="flex items-center gap-2 text-sm">
            <MonthSelect id="a" value={a} onChange={setA} months={options.mois} />
            <span className="text-slate-400">vs</span>
            <MonthSelect id="b" value={b} onChange={setB} months={options.mois} />
          </div>
          <FilterBar showMois={false} />
        </div>
      </PageHeader>
      {!options.mois.length ? <p className="text-sm text-slate-500" data-testid="compare-empty">Importez des données pour comparer des mois.</p> : !data ? <Loader2 className="h-6 w-6 animate-spin text-emerald-700" /> : (
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          <Panel title="Indicateurs" className="xl:col-span-3" testId="compare-table-panel">
            <div className="overflow-x-auto -mx-5">
              <SmartTable id="comparaison" className="w-full text-sm" data-testid="compare-table">
                <thead>
                  <tr className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-2 text-left">Indicateur</th>
                    <th className="px-3 text-right">{moisLabel(a)}</th>
                    <th className="px-3 text-right">{moisLabel(b)}</th>
                    <th className="px-5 text-right">Variation</th>
                  </tr>
                </thead>
                <tbody>
                  {METRICS.map(([k, label, kind, good]) => (
                    <tr key={k} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`compare-row-${k}`}>
                      <td className={`px-5 py-2.5 ${label.startsWith("dont") ? "pl-9 text-slate-500" : "font-medium"}`}>{label}</td>
                      <td className="px-3 text-right tabular" data-testid={`compare-a-${k}`}>{fmtVal(data.a[k], kind)}</td>
                      <td className="px-3 text-right tabular" data-testid={`compare-b-${k}`}>{fmtVal(data.b[k], kind)}</td>
                      <td className="px-5 text-right"><Variation v={data.variations[k]} good={good} testId={`compare-var-${k}`} /></td>
                    </tr>
                  ))}
                </tbody>
              </SmartTable>
            </div>
          </Panel>
          <Panel title="Vue graphique" className="xl:col-span-2" testId="compare-chart">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => money(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="A" name={moisLabel(a)} fill="#94A3B8" radius={[3, 3, 0, 0]} />
                <Bar dataKey="B" name={moisLabel(b)} fill="#00875A" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      )}
    </div>
  );
}
